import { rankWith, type ControlProps, type RankedTester } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { CATALOG_RENDERER_NAMES } from "../../shared/constants/catalogReviewConstants";
import { CaseFieldRow } from "../../shared/renderers/CaseReviewRenderers";
import { useCaseFieldReview, useCaseReviewContext } from "../../shared/hooks/useCaseFieldReview";
import { useAssessmentReference } from "../context/AssessmentReferenceContext";
import { AssessmentReferenceValue } from "@/features/reference_documents/shared/components/AssessmentReferenceValue";

/**
 * The listedness evidence column: local reference documents, a DailyMed link, or the plain
 * database id — whichever the row actually has. Read-only in every mode, like the id it replaces:
 * evidence is attached during ingestion or review of the datasheet row, never typed in here.
 *
 * <p>Identity comes from {@link useCaseReviewContext}'s {@code recordId}, which is the datasheet's
 * own row id once {@code datasheets} renders as its own nested record table — not the relationship
 * the datasheet belongs to, and never inferred from array position.
 */
function AssessmentReferenceControl(props: ControlProps) {
  const field = useCaseFieldReview(props);
  const { recordId } = useCaseReviewContext();
  const { dailyMedUrlByDatasheetRecordId, referenceDocumentsByDatasheetRecordId } = useAssessmentReference();
  const documents = recordId ? referenceDocumentsByDatasheetRecordId[recordId] : undefined;
  const dailyMedUrl = recordId ? dailyMedUrlByDatasheetRecordId[recordId] : undefined;
  const databaseId = typeof props.data === "string" ? props.data : "";

  return <CaseFieldRow props={props} field={field}>{() =>
    <AssessmentReferenceValue databaseId={databaseId} documents={documents}
      dailyMedUrl={dailyMedUrl} inputId={field.inputId} />}
  </CaseFieldRow>;
}

/** Ranked above the ordinary control, so only the field the catalog marks for it is affected. */
export const assessmentReferenceRendererTester: RankedTester = rankWith(11, (uischema) =>
  (uischema as { options?: { renderer?: string } }).options?.renderer === CATALOG_RENDERER_NAMES.DAILYMED_LINK);

export const assessmentReferenceRenderer = withJsonFormsControlProps(AssessmentReferenceControl);
